'use client';

import NextImage from '@/features/Image';
import { PlayIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Link from 'next/link';
import { useState } from 'react';

interface Video {
  slug: string;
  banner: string;
  title: string;
}

interface ShortVideo extends Video {
  views?: number;
}

interface Playlist {
  id: string;
  title: string;
  banner: string;
  video_count: number;
}

interface ZepidtubeHomeProps {
  videos?: Video[];
  shorts?: ShortVideo[];
  playlists?: Playlist[];
  updateValue: (value: string) => void;
}

export default function ZepidtubeHome({
  videos = [],
  shorts = [],
  playlists = [],
  updateValue,
}: ZepidtubeHomeProps) {
  return (
    <div>
      {/* Videos Section */}
      {videos.length > 0 && (
        <div className="container">
          <div className="videos padding-bottom">
            <h4 className="mb-4">Videos</h4>
            <div className="row g-4 justify-content-center">
              {videos.map((video, index) => (
                <div className="col-md-6 col-lg-4" key={index}>
                  <article className="post">
                    <figure className="post__media post__video">
                      <Link href={`/zepid-tube/${video.slug}`}>
                        <NextImage
                          src={video?.banner}
                          alt={video?.title}
                          width={1280}
                          height={500}
                          className="fade-in fadeIn"
                          style={{
                            animationDuration: '0.3s',
                            animationDelay: '0s',
                          }}
                        />
                      </Link>
                      <Link
                        href={`/zepid-tube/${video.slug}`}
                        className="btn__video btn__iframe"
                      >
                        <HugeiconsIcon icon={PlayIcon} size={28} />
                      </Link>
                    </figure>
                    <div className="post__body">
                      <h5 className="post__title fw-semibold">
                        <Link
                          href={`/zepid-tube/${video.slug}`}
                          className="btn__video btn__iframe"
                        >
                          {video?.title}
                        </Link>
                      </h5>
                    </div>
                  </article>
                </div>
              ))}
            </div>
            <div className="load-more text-center mt-4">
              <button
                onClick={() => {
                  updateValue('Videos');
                  window.scrollTo({ top: 150, behavior: 'smooth' });
                }}
                className="trk-btn trk-btn--primary trk-btn--md"
              >
                View More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shorts Section */}
      {shorts.length > 0 && (
        <div className="shorts padding-top padding-bottom bg-color">
          <div className="container">
            <h4 className="mb-4">Shorts</h4>
            <div className="row g-4 justify-content-center">
              {shorts.map((short_video, index) => (
                <div className="col-md-6 col-lg-3" key={index}>
                  <article className="post post--short">
                    <figure className="post__media post__video">
                      <Link href={`/zepid-tube/${short_video.slug}`}>
                        <NextImage
                          src={short_video?.banner}
                          alt={short_video?.title}
                          width={1280}
                          height={500}
                          className="fade-in fadeIn"
                          style={{
                            animationDuration: '0.3s',
                            animationDelay: '0s',
                          }}
                        />
                      </Link>
                      <Link
                        href={`/zepid-tube/${short_video.slug}`}
                        className="btn__video btn__iframe"
                      >
                        <HugeiconsIcon icon={PlayIcon} size={26} />
                      </Link>
                    </figure>
                  </article>
                </div>
              ))}
            </div>
            <div className="load-more text-center mt-4">
              <button
                onClick={() => {
                  updateValue('Shorts');
                  window.scrollTo({ top: 150, behavior: 'smooth' });
                }}
                className="trk-btn trk-btn--primary trk-btn--md"
              >
                View More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Playlists Section */}
      <div className="container">
        <div className="videos padding-top padding-bottom">
          <h4 className="mb-4">Multiple playlists</h4>
          <div className="row g-4 justify-content-center">
            {playlists.map((playlist, index) => (
              <div className="col-md-6 col-lg-3" key={index}>
                <article className="post">
                  <figure className="post__media post__video">
                    <Link href={`/zepid-tube/playlist-details/${playlist.id}`}>
                      <NextImage
                        src={playlist?.banner}
                        alt={playlist?.title}
                        width={1280}
                        height={500}
                        className="fade-in fadeIn"
                        style={{
                          animationDuration: '0.3s',
                          animationDelay: '0s',
                        }}
                      />
                    </Link>
                    <Link
                      href={`/zepid-tube/playlist-details/${playlist.id}`}
                      className="playlist__btn btn__iframe"
                    >
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M14.6666 2.5H1.33325C1.05992 2.5 0.833252 2.27333 0.833252 2C0.833252 1.72667 1.05992 1.5 1.33325 1.5H14.6666C14.9399 1.5 15.1666 1.72667 15.1666 2C15.1666 2.27333 14.9399 2.5 14.6666 2.5Z"
                            fill="#232529"
                          />
                          <path
                            d="M7.33325 6.5H1.33325C1.05992 6.5 0.833252 6.27333 0.833252 6C0.833252 5.72667 1.05992 5.5 1.33325 5.5H7.33325C7.60659 5.5 7.83325 5.72667 7.83325 6C7.83325 6.27333 7.60659 6.5 7.33325 6.5Z"
                            fill="#232529"
                          />
                          <path
                            d="M5.33325 10.5H1.33325C1.05992 10.5 0.833252 10.2733 0.833252 10C0.833252 9.72667 1.05992 9.5 1.33325 9.5H5.33325C5.60659 9.5 5.83325 9.72667 5.83325 10C5.83325 10.2733 5.60659 10.5 5.33325 10.5Z"
                            fill="#232529"
                          />
                          <path
                            d="M3.99992 14.5H1.33325C1.05992 14.5 0.833252 14.2733 0.833252 14C0.833252 13.7267 1.05992 13.5 1.33325 13.5H3.99992C4.27325 13.5 4.49992 13.7267 4.49992 14C4.49992 14.2733 4.27325 14.5 3.99992 14.5Z"
                            fill="#232529"
                          />
                          <path
                            d="M14.5733 5.12079C14.1799 4.82079 13.6399 4.76079 13.0066 4.93412L10.1066 5.72079C9.32661 5.93412 8.84661 6.56746 8.84661 7.36746V9.06746V11.5208C8.56661 11.3608 8.23994 11.2608 7.89327 11.2608C6.81994 11.2608 5.93994 12.1341 5.93994 13.2141C5.93994 14.2875 6.81327 15.1675 7.89327 15.1675C8.97327 15.1675 9.84661 14.2941 9.84661 13.2141V9.44746L14.1666 8.26746V10.5541C13.8866 10.3941 13.5599 10.2941 13.2133 10.2941C12.1399 10.2941 11.2599 11.1675 11.2599 12.2475C11.2599 13.3208 12.1333 14.2008 13.2133 14.2008C14.2933 14.2008 15.1666 13.3275 15.1666 12.2475V7.61412V6.58079C15.1666 5.90746 14.9666 5.41412 14.5733 5.12079ZM7.89327 14.1675C7.36661 14.1675 6.93994 13.7408 6.93994 13.2141C6.93994 12.6875 7.36661 12.2608 7.89327 12.2608C8.41994 12.2608 8.84661 12.6875 8.84661 13.2141C8.84661 13.7408 8.41994 14.1675 7.89327 14.1675ZM13.2133 13.2008C12.6866 13.2008 12.2599 12.7741 12.2599 12.2475C12.2599 11.7208 12.6866 11.2941 13.2133 11.2941C13.7399 11.2941 14.1666 11.7208 14.1666 12.2475C14.1666 12.7741 13.7399 13.2008 13.2133 13.2008Z"
                            fill="#232529"
                          />
                        </svg>
                      </span>
                      {playlist.video_count} Videos
                    </Link>
                  </figure>
                  <div className="post__body">
                    <h4 className="post__title">
                      <Link
                        href={`/zepid-tube/playlist-details/${playlist.id}`}
                        className="btn__video btn__iframe"
                      >
                        {playlist?.title}
                      </Link>
                    </h4>
                    <div className="post__footer">
                      <span className="post__footer-info">
                        View full playlist
                      </span>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
          <div className="load-more text-center mt-4">
            <button
              onClick={() => {
                updateValue('Playlists');
                window.scrollTo({ top: 150, behavior: 'smooth' });
              }}
              className="trk-btn trk-btn--primary trk-btn--md"
            >
              View More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
